import clsx from "clsx";
import { returnReferenceRateOptions } from "@/lib/businessRules/referenceRateRule";
import { SwapType } from "@/lib/businessRules/swapTypeRule";
import { returnFormattedDate } from "@/lib/globalFormatterHelper";
import { TradeInterestType } from "@/lib/interfaces and Enums/ENUMSwapTradeMessage";
import { ISwapLegTicket } from "@/store/tradeFromScratchStore";
import {
  SwapLegKey,
  isFixedLeg,
  returnCpiCommitChanges,
  returnEffectiveFirstCouponFrom,
  returnLegFrequencyChanges,
  returnZcsCpiIndex,
} from "../helper/swapTicketHelper";
import { ITicketFieldsProps } from "../helper/ticketSharedHelper";
import { useSwapReferenceLists } from "../helper/useSwapReferenceLists";
import CalendarSelect from "../ticketFields/CalendarSelect";
import ReferenceRateSelect from "../ticketFields/ReferenceRateSelect";
import NominalInput from "../ticketFields/NominalInput";
import TicketDateInput from "../ticketFields/TicketDateInput";
import TicketLegRow, { LEG_ROW_GRID } from "../ticketFields/TicketLegRow";
import TicketNumberInput from "../ticketFields/TicketNumberInput";
import TicketTenorDateInput from "../ticketFields/TicketTenorDateInput";
import TicketRow from "../ticketFields/TicketRow";
import TicketSelect from "../ticketFields/TicketSelect";
import { useSwapTicket } from "./useSwapTicket";
import { returnLegHolidayCalendars } from "../helper/holidayCalendarsHelper";
import { useZcsCpiAutoFill } from "../helper/useZcsCpiAutoFill";

// Swap dates and both legs, side by side so they read as a comparison. Every leg
// convention is defaulted per currency (swapConventionsRule). Becomes the Trade
// Creation Security Details panel content.

const INTEREST_TYPES: TradeInterestType[] = [
  TradeInterestType.Fixed,
  TradeInterestType.Floating,
];

const legTestId = (key: SwapLegKey, name: string) =>
  `SwapTicket${key === "leg1" ? "Leg1" : "Leg2"}${name}Input`;

const SwapDetailFields = ({ ticketId, highlightedKeys }: ITicketFieldsProps) => {
  const {
    ticket,
    updateSwapTicket,
    updateSwapLeg,
    handleLegConventionChange,
    swapType,
    isPerLegNominal,
    spreadLegs,
    dateIssue,
    requiredKeys,
  } = useSwapTicket(ticketId);
  // LQC-2957 - CPI filled from the backend for a ZCS, unless typed.
  const cpiSourceNote = useZcsCpiAutoFill(ticket, updateSwapTicket);
  const referenceLists = useSwapReferenceLists();

  const isMissing = (key: string) => !!highlightedKeys?.has(key);
  const isRequired = (key: string) => requiredKeys.has(key);

  // Both columns built from one definition so they cannot drift.
  const legRow = (
    label: string,
    testId: string,
    field: keyof ISwapLegTicket,
    render: (leg: ISwapLegTicket, legKey: SwapLegKey) => React.ReactNode,
  ) => (
    <TicketLegRow
      label={label}
      testId={testId}
      isLeg1Highlighted={isMissing(`leg1.${field}`)}
      isLeg2Highlighted={isMissing(`leg2.${field}`)}
      isRequired={isRequired(`leg1.${field}`) || isRequired(`leg2.${field}`)}
      leg1={render(ticket.leg1, "leg1")}
      leg2={render(ticket.leg2, "leg2")}
    />
  );

  return (
    <div
      data-testid="SwapDetailFieldsContainer"
      id="SwapDetailFieldsContainer"
      className="flex flex-col gap-2"
    >
      <TicketRow
        label="Start Date"
        testId="SwapTicketStartDateRow"
        isHighlighted={isMissing("startDate")}
        isRequired={isRequired("startDate")}
      >
        <TicketDateInput
          testId="SwapTicketStartDateInput"
          holidayCalendars={returnLegHolidayCalendars(ticket)}
          value={ticket.startDate}
          onChange={(startDate) => updateSwapTicket({ startDate })}
        />
      </TicketRow>

      {/* Follows the start date until the user sets it (spec F9); one value, both legs. */}
      <TicketRow label="First Coupon From" testId="SwapTicketFirstCouponFromRow">
        <TicketDateInput
          testId="SwapTicketFirstCouponFromInput"
          value={returnEffectiveFirstCouponFrom(ticket)}
          onChange={(firstCouponFrom) => updateSwapTicket({ firstCouponFrom })}
        />
      </TicketRow>

      <TicketRow
        label="Maturity"
        testId="SwapTicketMaturityRow"
        isHighlighted={isMissing("maturityDate")}
        isRequired={isRequired("maturityDate")}
        error={dateIssue?.message ?? ""}
      >
        <TicketTenorDateInput
          testId="SwapTicketMaturityInput"
          holidayCalendars={returnLegHolidayCalendars(ticket)}
          value={ticket.maturityDate}
          tenorStart={ticket.startDate}
          onChange={(maturityDate) => updateSwapTicket({ maturityDate })}
        />
      </TicketRow>

      <TicketRow label="Trade Date" testId="SwapTicketTradeDateRow">
        <div className="text-xs dark:text-gray-200">
          {returnFormattedDate(ticket.tradeDate)}
        </div>
      </TicketRow>

      <TicketRow label="Cleared" testId="SwapTicketClearedRow">
        <div className="flex items-center gap-2 pb-1">
          <input
            data-testid="SwapTicketClearedInput"
            id="SwapTicketClearedInput"
            type="checkbox"
            checked={ticket.cleared}
            onChange={(e) => updateSwapTicket({ cleared: e.target.checked })}
            className="h-4 w-4"
          />
          {/* LCH is the only clearing house, so this states it rather than offering one. */}
          {ticket.cleared && (
            <span
              data-testid="SwapTicketClearingHouse"
              id="SwapTicketClearingHouse"
              className="text-xs dark:text-gray-200"
            >
              {ticket.clearingHouse}
            </span>
          )}
        </div>
      </TicketRow>

      {/* LQC-2957 - ZCS only, hidden entirely otherwise. Filled from the backend, still editable. */}
      {swapType === SwapType.ZCS && (
        <>
          <TicketRow
            label="CPI"
            testId="SwapTicketCpiRow"
            isHighlighted={isMissing("cpi")}
          >
            <TicketNumberInput
              testId="SwapTicketCpiInput"
              value={ticket.cpi}
              placeholder="e.g. 131.2"
              onCommit={(cpi) => updateSwapTicket(returnCpiCommitChanges(ticket, cpi))}
            />
          </TicketRow>
          <TicketRow label="CPI Index" testId="SwapTicketCpiIndexRow">
            <div className="text-xs dark:text-gray-200">
              {returnZcsCpiIndex(ticket) || "-"}
              {cpiSourceNote && (
                <span className="ml-2 text-gray-500 dark:text-gray-400">({cpiSourceNote})</span>
              )}
            </div>
          </TicketRow>
        </>
      )}

      {/* Shares the row grid so the headings sit over their legs. */}
      <div
        data-testid="SwapTicketLegHeader"
        id="SwapTicketLegHeader"
        className={clsx(
          LEG_ROW_GRID,
          "border-b border-gray-300 pb-1.5 pt-2 dark:border-gray-600",
        )}
      >
        <div />
        <div className="px-1 text-xs font-semibold dark:text-gray-200">Leg 1</div>
        <div className="px-1 text-xs font-semibold dark:text-gray-200">Leg 2</div>
      </div>

      {/* LQC-2971 - cross-currency legs carry their own notional. Leg 1 is the trade nominal. */}
      {isPerLegNominal && (
        <TicketLegRow
          label="Nominal"
          testId="SwapTicketLegNominalRow"
          isLeg1Highlighted={isMissing("nominal")}
          isLeg2Highlighted={isMissing("nominal2")}
          isRequired
          leg1={
            <NominalInput
              testId={legTestId("leg1", "Nominal")}
              value={ticket.nominal}
              onCommit={(nominal) => updateSwapTicket({ nominal })}
            />
          }
          leg2={
            <NominalInput
              testId={legTestId("leg2", "Nominal")}
              value={ticket.nominal2}
              onCommit={(nominal2) => updateSwapTicket({ nominal2 })}
            />
          }
        />
      )}

      {legRow("Type", "SwapTicketInterestTypeRow", "interestType", (leg, key) => (
        <TicketSelect<TradeInterestType>
          testId={legTestId(key, "InterestType")}
          value={leg.interestType}
          options={INTEREST_TYPES}
          includeEmptyOption={false}
          onChange={(interestType) =>
            handleLegConventionChange(key, { interestType })
          }
        />
      ))}

      {legRow("Currency", "SwapTicketCurrencyRow", "currency", (leg, key) => (
        <TicketSelect<string>
          testId={legTestId(key, "Currency")}
          value={leg.currency}
          options={referenceLists.currencies}
          includeEmptyOption={false}
          onChange={(currency) => handleLegConventionChange(key, { currency })}
        />
      ))}

      {/* One row for both rates: a leg has a coupon or a spread, never both. Spread
          only on the legs returnSpreadLegs allows (LQC-2967). */}
      {legRow("Rate / Spread", "SwapTicketRateRow", "couponRate", (leg, key) =>
        !isFixedLeg(leg) && !spreadLegs[key] ? (
          <div className="px-1 text-xs text-gray-400">-</div>
        ) : (
          <TicketNumberInput
            testId={legTestId(key, "Rate")}
            value={isFixedLeg(leg) ? leg.couponRate : leg.spread}
            placeholder={isFixedLeg(leg) ? "e.g. 2.675" : "spread"}
            onCommit={(value) =>
              updateSwapLeg(
                key,
                isFixedLeg(leg) ? { couponRate: value } : { spread: value },
              )
            }
          />
        ),
      )}

      {legRow(
        "Reference Rate",
        "SwapTicketReferenceRateRow",
        "referenceRate",
        (leg, key) =>
          isFixedLeg(leg) ? (
            <div className="px-1 text-xs text-gray-400">-</div>
          ) : (
            <ReferenceRateSelect
              testId={legTestId(key, "ReferenceRate")}
              value={leg.referenceRate}
              currency={leg.currency}
              options={returnReferenceRateOptions(
                leg.currency,
                swapType === SwapType.ZCS,
                leg.couponFrequency,
              )}
              onChange={(referenceRate) =>
                updateSwapLeg(key, { referenceRate })
              }
            />
          ),
      )}

      {legRow("Frequency", "SwapTicketFrequencyRow", "couponFrequency", (leg, key) => (
        <TicketSelect<string>
          testId={legTestId(key, "Frequency")}
          value={leg.couponFrequency}
          options={referenceLists.frequencies}
          includeEmptyOption={false}
          onChange={(couponFrequency) =>
            updateSwapTicket(returnLegFrequencyChanges(ticket, key, couponFrequency))
          }
        />
      ))}

      {legRow("Reset Freq", "SwapTicketResetFrequencyRow", "resetFrequency", (leg, key) =>
        isFixedLeg(leg) ? (
          <div className="px-1 text-xs text-gray-400">-</div>
        ) : (
          <TicketSelect<string>
            testId={legTestId(key, "ResetFrequency")}
            value={leg.resetFrequency}
            options={referenceLists.frequencies}
            includeEmptyOption={false}
            onChange={(resetFrequency) => updateSwapLeg(key, { resetFrequency })}
          />
        ),
      )}

      {legRow("Day Count", "SwapTicketDayCountRow", "dayCount", (leg, key) => (
        <TicketSelect<string>
          testId={legTestId(key, "DayCount")}
          value={leg.dayCount}
          options={referenceLists.dayCounts}
          includeEmptyOption={false}
          onChange={(dayCount) => updateSwapLeg(key, { dayCount })}
        />
      ))}

      {legRow("Business Day", "SwapTicketBusinessDayRow", "businessDay", (leg, key) => (
        <TicketSelect<string>
          testId={legTestId(key, "BusinessDay")}
          value={leg.businessDay}
          options={referenceLists.businessDays}
          includeEmptyOption={false}
          onChange={(businessDay) => updateSwapLeg(key, { businessDay })}
        />
      ))}

      {legRow("Calendar", "SwapTicketCalendarRow", "calendar", (leg, key) => (
        <CalendarSelect
          testId={legTestId(key, "Calendar")}
          value={leg.calendar}
          calendars={referenceLists.calendars}
          isReady={referenceLists.isCalendarListReady}
          onChange={(calendar) => updateSwapLeg(key, { calendar })}
        />
      ))}
    </div>
  );
};

export default SwapDetailFields;
